import { Injectable } from "@angular/core";
import { PermitStep } from "@ha/data/basic";
import { Action, State, StateContext, Store } from "@ngxs/store";
import produce from "immer";
import { tap } from "rxjs";

import { Navigate } from "@ngxs/router-plugin";
import { CustomerQuestionAnswer } from "../../permit-create/models/question.model";
import { PermitCreateActions } from "../../permit-create/states/permit-create.actions";
import { PermitCreateSelectors } from "../../permit-create/states/permit-create.selectors";
import { PermitQuestionActions } from "../../permit-create/states/permit-question.actions";
import { SaveLocalQuestion } from "../../permit-create/states/permit-question-storage.state";
import { PermitPhotoActions } from "../../permit-create/states/permit-photo.actions";
import { PermitDetailsActions } from "../../permit-details/states/permit-details.action";
import { PermitPaths } from "../../permit-paths";
import { CurrentStep } from "../models/current-step.model";
import { PermitStepService } from "../services/permit-step.service";
import { PermitStepActions } from "./permit-step.action";

export interface PermitStepStateModel {
    currentStep: CurrentStep;
    permitId: number;
    isPremium: boolean;
    isDemo: boolean;
    gasfreeCertificationAnswer?: CustomerQuestionAnswer;
}

const defaults = {
    currentStep: {
        step: undefined,
        currentQuestionId: undefined,
    },
    permitId: undefined,
    isPremium: false,
    isDemo: false,
    gasfreeCertificationAnswer: undefined,
};

@Injectable()
@State<PermitStepStateModel>({
    name: "permitStep",
    defaults: defaults,
})
export class PermitStepState {
    constructor(private permitStepService: PermitStepService, private store: Store) {}

    @Action(PermitStepActions.Reset)
    public reset(ctx: StateContext<PermitStepStateModel>) {
        ctx.setState(defaults);
    }

    @Action(PermitStepActions.StartCreate)
    public startCreate(ctx: StateContext<PermitStepStateModel>, action: PermitStepActions.StartCreate) {
        ctx.dispatch(new PermitCreateActions.ResetToDefault());
        ctx.setState(defaults);
        ctx.patchState({
            currentStep: {
                step: PermitStep.isFlammableHotWork,
            },
            isPremium: action.isPremium,
            isDemo: action.isDemo,
        });
    }

    @Action(PermitStepActions.GetCurrentStep)
    public getCurrentStep(ctx: StateContext<PermitStepStateModel>, action: PermitStepActions.GetCurrentStep) {
        const permitId = !action.permitId ? ctx.getState().permitId : action.permitId;
        return this.permitStepService.getCurrentStep(permitId).pipe(tap((result) => {
            ctx.setState(produce((draft) => {
                if (draft.permitId !== permitId) {
                    draft.gasfreeCertificationAnswer = undefined;
                }
                draft.currentStep = result;
                draft.permitId = permitId;
            }));
        }));
    }

    @Action(PermitStepActions.GoBackwards)
    public previousStep(ctx: StateContext<PermitStepStateModel>, action: PermitStepActions.GoBackwards) {
        const permitId = !action.permitId ? ctx.getState().permitId : action.permitId;
        const previousStep = ctx.getState().currentStep.step;

        return this.permitStepService.setPreviousStep(permitId, action.currentQuestionId).pipe(tap((result) => {
            // Clear comments and photos only on successful navigation
            ctx.dispatch(new PermitDetailsActions.ClearComments());
            ctx.dispatch(new PermitPhotoActions.ClearPhotos());
            this.resetQuestionNavigationState(permitId, result.currentQuestionId);

            ctx.setState(produce((draft) => {
                if (draft.permitId !== permitId) {
                    draft.gasfreeCertificationAnswer = undefined;
                }

                if (result.step === PermitStep.gasFreeCertification) {
                    draft.gasfreeCertificationAnswer = this.getGasfreeCertificationAnswerFromBackNavigation(previousStep);
                }

                draft.currentStep = result;
                draft.permitId = permitId;
            }));
            if (result.step === PermitStep.preparationComplete) {
                this.store.dispatch(new Navigate([PermitPaths.Root, PermitPaths.PreparedPermits]));
            }
        }));
    }

    @Action(PermitStepActions.GoForward)
    public nextStep(ctx: StateContext<PermitStepStateModel>, action: PermitStepActions.GoForward) {
        const permitId = !action.permitId ? ctx.getState().permitId : action.permitId;
        const previousStep = ctx.getState().currentStep.step;
        return this.permitStepService.setNextStep(permitId, undefined, action.currentQuestionId).pipe(tap((result) => {
            // Clear comments and photos only on successful navigation
            ctx.dispatch(new PermitDetailsActions.ClearComments());
            ctx.dispatch(new PermitPhotoActions.ClearPhotos());
            this.resetQuestionNavigationState(permitId);

            ctx.setState(produce((draft) => {
                if (draft.permitId !== permitId) {
                    draft.gasfreeCertificationAnswer = undefined;
                }
                draft.currentStep = result;
                draft.permitId = permitId;
            }));
            if (previousStep === PermitStep.workplace) {
                this.store.dispatch(new PermitDetailsActions.GetBasicDetails(permitId));
            }

            const isSuperintendentFlow = this.store.selectSnapshot(PermitCreateSelectors.isSuperintendentFlow) === true;
            if (previousStep === PermitStep.signing && result.step === PermitStep.started && isSuperintendentFlow) {
                this.store.dispatch(new PermitDetailsActions.ShowFlammableAtexPermitCreated(true));
                this.store.dispatch(new PermitDetailsActions.ShowPermitStarted(true));
            } else {
                this.store.dispatch(new PermitDetailsActions.ShowFlammableAtexPermitCreated(false));
            }
        }));
    }

    @Action(PermitStepActions.GoToValidity)
    public gotoValidity(ctx: StateContext<PermitStepStateModel>, action: PermitStepActions.GoForward) {
        const permitId = !action.permitId ? ctx.getState().permitId : action.permitId;

        return this.permitStepService.setNextStep(permitId).pipe(tap((result) => {
            // Clear comments and photos only on successful navigation
            ctx.dispatch(new PermitDetailsActions.ClearComments());
            ctx.dispatch(new PermitPhotoActions.ClearPhotos());
            this.resetQuestionNavigationState(permitId);

            ctx.setState(produce((draft) => {
                if (draft.permitId !== permitId) {
                    draft.gasfreeCertificationAnswer = undefined;
                }
                draft.currentStep = result;
                draft.permitId = permitId;
            }));
        }));
    }

    @Action(PermitStepActions.CreateGasfreeCert)
    public createGasfreeCert(ctx: StateContext<PermitStepStateModel>, action: PermitStepActions.CreateGasfreeCert) {
        const permitId = !action.permitId ? ctx.getState().permitId : action.permitId;
        return this.permitStepService.setNextStep(permitId, CustomerQuestionAnswer.Yes).pipe(tap((result) => {
            this.resetQuestionNavigationState(permitId);

            ctx.setState(produce((draft) => {
                draft.gasfreeCertificationAnswer = CustomerQuestionAnswer.Yes;
                draft.currentStep = result;
                draft.permitId = permitId;
            }));
        }));
    }

    @Action(PermitStepActions.SetGasfreeCertAnswer)
    public setGasfreeCertAnswer(
        ctx: StateContext<PermitStepStateModel>,
        action: PermitStepActions.SetGasfreeCertAnswer,
    ): void {
        ctx.setState(produce((draft) => {
            draft.gasfreeCertificationAnswer = action.answer;
        }));
    }

    private resetQuestionNavigationState(permitId: number, currentQuestionId?: number): void {
        this.store.dispatch([
            new PermitQuestionActions.ResetToDefault(),
            new SaveLocalQuestion(permitId, currentQuestionId),
        ]);
    }

    private getGasfreeCertificationAnswerFromBackNavigation(
        previousStep: PermitStep,
    ): CustomerQuestionAnswer | undefined {
        switch (previousStep) {
            case PermitStep.gasFreeCertificatesFixedQuestions:
                return CustomerQuestionAnswer.Yes;
            case PermitStep.flammableMaterialsFixedQuestions:
                return CustomerQuestionAnswer.No;
            default:
                return undefined;
        }
    }
}
